import * as React from 'react';
import { Minus, Plus, Trash2 } from 'lucide-react';
import { Button } from './Button';
import { DataValue } from './DataValue';
import { Inline, Stack } from './Layout';
import type { LineItem } from './LineItemsTable';
import { KeyValueRow, ListRow } from './List';
import { Text } from './Text';

export interface LineItemListProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  items: LineItem[];
  /**
   * Called with a line's new quantity from its − and + buttons, which show
   * only when this is given. A quantity does not go below one; the bin
   * removes a line.
   */
  onQuantityChange?: (index: number, qty: number) => void;
  /** Puts a bin on each line, which calls this. */
  onRemove?: (index: number) => void;
  /** The total under the lines. Left out, it is the sum of the lines. */
  total?: number;
  /** What shows when there are no lines. */
  emptyLabel?: string;
}

const money = (value: number) => <DataValue value={value} format="currency" />;

/**
 * An invoice's lines on a phone: each charge's name with its quantity and
 * price under it and its amount at the right, ruled off from the next, and
 * the total under them. Given `onQuantityChange` and `onRemove` it is the
 * create sheet's editable list, a − and + and a bin on each line.
 *
 *   <LineItemList items={lines} onQuantityChange={setQty} onRemove={remove} />
 *   <LineItemList items={invoice.items} total={invoice.total} />
 *
 * LineItemsTable is the desk's: five columns of inputs, which do not fit a
 * phone. Here a line's name and price are set when it is added, so only the
 * quantity changes in place.
 */
export const LineItemList = React.forwardRef<HTMLDivElement, LineItemListProps>(
  function LineItemList(
    {
      items,
      onQuantityChange,
      onRemove,
      total,
      emptyLabel = 'No charges yet',
      ...props
    },
    ref
  ) {
    const sum =
      total ?? items.reduce((all, item) => all + item.qty * item.rate, 0);
    return (
      <Stack ref={ref} gap={0} divided {...props}>
        {items.length === 0 ? (
          <ListRow inset="ruled">
            <Text tone="muted">{emptyLabel}</Text>
          </ListRow>
        ) : null}
        {items.map((item, index) => (
          <ListRow key={index} inset="ruled">
            <Stack gap={0} grow>
              <Text as="span" variant="body-strong">
                {item.description}
              </Text>
              <Text as="span" variant="caption" tone="muted" numeric>
                {item.qty} × {money(item.rate)}
              </Text>
            </Stack>
            {onQuantityChange ? (
              <Inline gap={1} align="center">
                <Button
                  variant="subtle"
                  size="icon-sm"
                  pill
                  aria-label={`One fewer ${item.description}`}
                  disabled={item.qty <= 1}
                  onClick={() => onQuantityChange(index, item.qty - 1)}
                >
                  <Minus />
                </Button>
                <Text as="span" variant="body-strong" numeric>
                  {item.qty}
                </Text>
                <Button
                  variant="subtle"
                  size="icon-sm"
                  pill
                  aria-label={`One more ${item.description}`}
                  onClick={() => onQuantityChange(index, item.qty + 1)}
                >
                  <Plus />
                </Button>
              </Inline>
            ) : null}
            <Text as="span" variant="body-strong" numeric>
              {money(item.qty * item.rate)}
            </Text>
            {onRemove ? (
              <Button
                variant="destructive-ghost"
                size="icon-sm"
                aria-label={`Remove ${item.description}`}
                onClick={() => onRemove(index)}
              >
                <Trash2 />
              </Button>
            ) : null}
          </ListRow>
        ))}
        <KeyValueRow
          label="Total"
          value={sum}
          format="currency"
          emphasis
          density="relaxed"
        />
      </Stack>
    );
  }
);
